import { useState, useMemo, useEffect } from 'react'
import { Modal } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'

interface Field {
  key: string
  label: string
  type: 'text' | 'number' | 'date'
  value: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  initialTemplate: string
  availableFields: Field[]
  onSave: (message: string) => void
  title?: string
}

export function WhatsAppTemplateModal({
  isOpen,
  onClose,
  initialTemplate,
  availableFields,
  onSave,
  title,
}: Props) {
  const [template, setTemplate] = useState(initialTemplate)
  const [draggedField, setDraggedField] = useState<Field | null>(null)

  const { t } = useTranslation()
  const tc = (key: string, params?: Record<string, string>) => t(`billing.contacts.${key}`, params)
  const modalTitle = title ?? tc('templateModal.titleDefault')

  useEffect(() => {
    setTemplate(initialTemplate)
  }, [initialTemplate])

  const variables = useMemo(() => {
    const matches = template.match(/\{\{[\w_]+\}\}/g) || []
    return matches.map(m => m.replace(/[{}]/g, '').trim())
  }, [template])

  const variableFields = useMemo(() => {
    return variables
      .map(varName => availableFields.find(f => f.key === varName))
      .filter((f) => f !== undefined) as Field[]
  }, [variables, availableFields])

  const renderPreview = () => {
    let preview = template

    variableFields.forEach((field) => {
      const placeholder = `{{${field.key}}}`
      const index = preview.indexOf(placeholder)
      if (index !== -1) {
        const before = preview.substring(0, index)
        const after = preview.substring(index + placeholder.length)
        preview = before + `\x00${field.key}\x00` + after
      }
    })

    return (
      <div className="whitespace-pre-wrap break-words text-sm text-slate-900 dark:text-white space-y-3">
        {preview.split('\x00').map((part, i) => {
          const isVariable = i % 2 === 1
          if (isVariable) {
            const field = availableFields.find(f => f.key === part)
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 whitespace-nowrap"
              >
                {field?.label || `[${part}]`}
                <button
                  onClick={() => removeVariable(part)}
                  className="ml-0.5 hover:bg-emerald-200 dark:hover:bg-emerald-800 rounded p-0.5"
                >
                  ✕
                </button>
              </span>
            )
          }
          return part
        })}
      </div>
    )
  }

  const removeVariable = (varName: string) => {
    setTemplate(template.replace(`{{${varName}}}`, ''))
  }

  const handleDragStart = (e: React.DragEvent, field: Field) => {
    try {
      e.dataTransfer?.setData('text/plain', field.key)
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy'
    } catch {}
    setDraggedField(field)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    try { if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy' } catch {}
    e.currentTarget.classList.add('bg-slate-50', 'dark:bg-slate-700')
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-slate-50', 'dark:bg-slate-700')
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.currentTarget.classList.remove('bg-slate-50', 'dark:bg-slate-700')

    const keyFromData = (e.dataTransfer && e.dataTransfer.getData)
      ? e.dataTransfer.getData('text/plain')
      : ''

    let field: Field | null = null
    if (keyFromData) {
      field = availableFields.find(f => f.key === keyFromData) || null
    }
    if (!field && draggedField) field = draggedField
    if (!field) return

    const placeholder = `{{${field.key}}}`

    const ta = document.getElementById('wa-template-textarea') as HTMLTextAreaElement | null
    if (ta) {
      const start = typeof ta.selectionStart === 'number' ? ta.selectionStart : ta.value.length
      const end = typeof ta.selectionEnd === 'number' ? ta.selectionEnd : start
      const before = ta.value.substring(0, start)
      const after = ta.value.substring(end)
      const newVal = before + placeholder + after
      ta.value = newVal
      setTemplate(newVal)
      const pos = before.length + placeholder.length
      ta.selectionStart = ta.selectionEnd = pos
      ta.focus()
    } else {
      setTemplate(t => t + placeholder)
    }

    setDraggedField(null)
  }

  const handleSave = () => {
    if (template.trim()) {
      onSave(template)
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      headerRight={
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={!template.trim()}
            title={t('actions.save')}
            className={`flex items-center gap-2 transition-colors ${!template.trim() ? 'text-slate-300 cursor-not-allowed' : 'text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100'}`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            onClick={onClose}
            aria-label={t('actions.close')}
            className="hidden md:inline-flex p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <svg className="h-5 w-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:h-[500px] h-auto">

        {/* Preview (mobile: first, desktop: second row left) */}
        <div className="md:col-start-1 md:col-span-2 md:row-start-2">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-3 overflow-y-auto transition-colors"
          >
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
              {tc('templateModal.preview')}
            </p>
            {renderPreview()}
          </div>
        </div>

        {/* Available fields panel - penultimate on mobile, right column on desktop */}
        <div className="md:col-start-3 md:row-start-1 md:row-span-2 flex flex-col gap-2">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {tc('templateModal.availableFields')}
          </p>
          <div className="flex-1 space-y-2 overflow-y-auto">
            {availableFields.map((field) => (
              <div
                key={field.key}
                draggable
                onDragStart={(e) => handleDragStart(e, field)}
                onDragEnd={() => setDraggedField(null)}
                onClick={() => {
                  const placeholder = `{{${field.key}}}`
                  const ta = document.getElementById('wa-template-textarea') as HTMLTextAreaElement | null
                  if (ta && ta.selectionStart !== undefined) {
                    const start = ta.selectionStart
                    const end = ta.selectionEnd
                    const before = ta.value.substring(0, start)
                    const after = ta.value.substring(end)
                    ta.value = before + placeholder + after
                    ta.focus()
                    const pos = before.length + placeholder.length
                    ta.selectionStart = ta.selectionEnd = pos
                    setTemplate(ta.value)
                  } else {
                    setTemplate(t => t + placeholder)
                  }
                }}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 cursor-move hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                <div className="text-xs font-medium text-slate-900 dark:text-white">
                  {field.label}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                  {`{{${field.key}}}`}
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 italic">
            {tc('templateModal.insertHint')}
          </div>
        </div>

        {/* Text editor (mobile: last, desktop: first row left) */}
        <div className="md:col-start-1 md:col-span-2 md:row-start-1 flex flex-col gap-3">
          <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('templateModal.messageLabel')}
            </label>
            <textarea
              id="wa-template-textarea"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              placeholder={tc('templateModal.placeholder')}
              className="w-full h-32 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
